import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { useIxmUserauthV1InvitationServiceCheckOrganizationMemberInvitation } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-invitation-service-check-organization-member-invitation';
import { useRegister } from '@interxfi/api-client/userauth/register';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box, DEFAULT_BOX_PADDING_NO_HORIZONTAL } from '@interxfi/design-system/components/box/box';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Typography, type TypographyProps } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1OrganizationMemberInvitationState, IxmUserauthV1SessionServiceRegisterRequest } from '@interxfi/types/generated/api';
import { encodeToBase64 } from '@interxfi/utilities/encoding/base64';
import { AppTextInputWithLens } from '../../components/form/app-text-input';
import { AuthLayout } from '../../features/auth/layout';
import { UserauthPasswordInputWithLens, userauthPasswordValidator } from '../../features/userauth/user/password/userauth-password-input';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../util/error-handler';
import { DEFAULT_UNAUTHENTICATED_ROUTE, MFA_SETUP_ROUTE } from '../../util/routing/defaults';
import { getPageHead } from '../../util/routing/meta';

const registerSearchSchema = z.object({
  id: z.string().optional(),
  secret: z.string().optional(),
  organizationId: z.string().optional(),
});

export const Route = createFileRoute('/_auth/register')({
  validateSearch: registerSearchSchema,
  head: () => getPageHead('Register'),
  component: Register,
});

const registerFormSchema = z.object({
  email: z.email({ error: 'Email must be a valid email' }).nonempty({ error: 'Email is required' }),
  password: userauthPasswordValidator,
  firstName: z.string().nonempty({ error: 'First Name is required' }),
  lastName: z.string().nonempty({ error: 'Last Name is required' }),
});

type RegisterFormValues = z.infer<typeof registerFormSchema>;

function getDefaultValues(invitation: IxmUserauthV1OrganizationMemberInvitationState | undefined): RegisterFormValues {
  return {
    email: invitation?.data?.email?.address ?? '',
    password: '',
    firstName: invitation?.data?.profile?.firstName ?? '',
    lastName: invitation?.data?.profile?.lastName ?? '',
  };
}

function getLoginLink(invitationId: string | undefined, secret: string | undefined, organizationId: string | undefined) {
  if (invitationId && secret && organizationId) {
    return `${DEFAULT_UNAUTHENTICATED_ROUTE}?redirectTo=${encodeToBase64(`/accept-invitation?id=${invitationId}&secret=${secret}&organizationId=${organizationId}`)}`;
  }

  return DEFAULT_UNAUTHENTICATED_ROUTE;
}

const typographyProps = {
  lineHeight: '1.125rem',
  size: '0.875rem',
} as const satisfies TypographyProps;

const registerErrorMessageGetter = buildDefaultErrorMessageGetter(
  'An error occurred while creating your account. Please try again.',
  'create an account',
);
const getInvitationErrorMessageGetter = buildDefaultErrorMessageGetter(
  'Your invitation was not found. Please contact your organization administrator.',
  'get your invitation details',
);

function Register() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = Route.useNavigate();
  const { id: invitationId, secret, organizationId } = Route.useSearch();
  const { mutateAsync, isPending, error } = useRegister();
  useErrorHandler(error, registerErrorMessageGetter);

  const { data: invitationResponse, error: invitationError } = useIxmUserauthV1InvitationServiceCheckOrganizationMemberInvitation(
    invitationId && secret
      ? {
          token: { secret, id: invitationId },
        }
      : undefined,
  );
  useErrorHandler(invitationError, getInvitationErrorMessageGetter);

  const [buttonState, buttonRef] = useButtonState(isPending, error);

  const form = useForm({
    defaultValues: getDefaultValues(invitationResponse?.invitation),
    mode: 'all',
    resolver: zodResolver(registerFormSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
  } = form;

  const lens = useLens({ control });

  const setFormValuesFromInvitation = useEffectEvent((invitation: IxmUserauthV1OrganizationMemberInvitationState) => {
    if (invitation.data.email) {
      setValue('email', invitation.data.email.address || '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }

    if (invitation.data.profile?.firstName) {
      setValue('firstName', invitation.data.profile.firstName, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }

    if (invitation.data.profile?.lastName) {
      setValue('lastName', invitation.data.profile.lastName, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  });

  // Seed form values
  useEffect(() => {
    if (invitationResponse?.invitation) {
      setFormValuesFromInvitation(invitationResponse.invitation);
    }
  }, [invitationResponse?.invitation]);

  async function onSubmit(value: RegisterFormValues) {
    try {
      setIsSubmitting(true);

      const { firstName, lastName, ...rest } = value;
      const submission: IxmUserauthV1SessionServiceRegisterRequest = {
        ...rest,
        profile: { firstName, lastName },
      };

      if (invitationResponse?.invitation && secret && invitationId) {
        submission.acceptInvitation = { id: invitationId, secret };
      }

      await mutateAsync(submission);
      await navigate({ to: MFA_SETUP_ROUTE });
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  }

  const invitationHint = useMemo(() => {
    if (invitationResponse?.invitation) {
      return {
        size: 'small',
        variant: 'primary',
        withBorderRadius: true,
        children: (
          <span>
            You've been invited to join{' '}
            <Typography weight="bold">{invitationResponse?.invitation?.data?.organization?.name || 'an organization'}</Typography> on Interchange.
          </span>
        ),
      } as const;
    }

    return undefined;
  }, [invitationResponse?.invitation]);

  return (
    <FormProvider {...form}>
      <AuthLayout hint={invitationHint}>
        <HorizontalStack align="space-between" blockAlign="end">
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
            Create an Account
          </Typography>

          <TextLink LinkElement={Link} size="medium" to={getLoginLink(invitationId, secret, organizationId)} underline="hover">
            Sign In
          </TextLink>
        </HorizontalStack>

        <VerticalStack as="form" gap={4} onSubmit={handleSubmit(onSubmit)}>
          <Grid autoColumns={2} gap={4}>
            <AppTextInputWithLens label="First Name" lens={lens.focus('firstName')} required />
            <AppTextInputWithLens label="Last Name" lens={lens.focus('lastName')} required />
          </Grid>

          <AppTextInputWithLens disabled={Boolean(invitationResponse?.invitation)} label="Email" lens={lens.focus('email')} required />

          <UserauthPasswordInputWithLens label="Password" lens={lens.focus('password')} required />

          <Box padding={DEFAULT_BOX_PADDING_NO_HORIZONTAL}>
            <Typography {...typographyProps}>
              By proceeding, you agree to our{' '}
              <TextLink to="https://interchange.com/disclosures" typography={typographyProps} underline="always">
                Terms &amp; Conditions
              </TextLink>
              .
            </Typography>
          </Box>

          <Button disabled={!isValid || isSubmitting} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
            Create Account
          </Button>
        </VerticalStack>
      </AuthLayout>
    </FormProvider>
  );
}
